import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import { getFavoritos, addFavorito, removeFavorito } from '../api/favoritos';

// Favoritos requiere cuenta -- sin sesión, el set queda vacío y
// toggleFavorito no se llama (FavoritoButton manda al login en ese caso).
const FavoritosContext = createContext(null);

export const FavoritosProvider = ({ children }) => {
  const { isLoggedIn } = useAuth();
  const [favoritoIds, setFavoritoIds] = useState(new Set());

  const refresh = useCallback(() => {
    if (!isLoggedIn) {
      setFavoritoIds(new Set());
      return Promise.resolve();
    }
    return getFavoritos()
      .then((items) => setFavoritoIds(new Set(items.map((item) => item.id))))
      .catch(() => {});
  }, [isLoggedIn]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavorito = useCallback((productoId) => favoritoIds.has(productoId), [favoritoIds]);

  const toggleFavorito = useCallback(
    async (productoId) => {
      if (favoritoIds.has(productoId)) {
        await removeFavorito(productoId);
      } else {
        await addFavorito(productoId);
      }
      await refresh();
    },
    [favoritoIds, refresh],
  );

  const value = { favoritoIds, isFavorito, toggleFavorito, refresh };

  return <FavoritosContext.Provider value={value}>{children}</FavoritosContext.Provider>;
};

FavoritosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFavoritos = () => {
  const context = useContext(FavoritosContext);
  if (!context) {
    throw new Error('useFavoritos debe usarse dentro de un FavoritosProvider');
  }
  return context;
};
